import { useMemo, useRef } from 'react';

import { useMountEffect } from '@/shared/hooks/useMountEffect';

export interface ManagedTimeoutHandle {
  /**
   * Schedule `callback` to fire after `delayMs`. If a previous timeout is
   * still pending it is cleared first — the last call wins.
   */
  start: (callback: () => void, delayMs: number) => void;
  /**
   * Cancel any pending timeout. Safe to call when idle.
   */
  clear: () => void;
  /**
   * True while a timeout is currently scheduled (post-`start`, pre-fire /
   * pre-`clear`).
   */
  isPending: () => boolean;
}

/**
 * Owns a single `setTimeout` handle + its unmount cleanup so consumers
 * don't need to write `useEffect(() => () => clearTimeout(ref.current), [])`
 * (project rule — see `.claude/skills/no-use-effect/SKILL.md`).
 *
 * On unmount the pending timeout, if any, is cleared automatically, and a
 * later `start` does nothing — so scheduled callbacks NEVER run after the
 * owning component tears down.
 *
 * The handle's methods are stable across re-renders, so it can be captured
 * in memoized callbacks without churning identity.
 *
 * This hook is the sanctioned escape hatch from the project's
 * "no direct `useEffect`" rule — `useEffect` may appear directly ONLY
 * inside reusable custom hooks.
 */
export function useManagedTimeout(): ManagedTimeoutHandle {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // `start` from a child's unmount cleanup runs after this hook's cleanup, so
  // without the flag it would arm a timer nothing clears.
  const disposedRef = useRef(false);

  const handle = useMemo<ManagedTimeoutHandle>(() => {
    const clear = () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };
    return {
      start: (callback, delayMs) => {
        clear();
        if (disposedRef.current) return;
        timerRef.current = setTimeout(() => {
          timerRef.current = undefined;
          callback();
        }, delayMs);
      },
      clear,
      isPending: () => timerRef.current !== undefined,
    };
  }, []);

  // Unmount cleanup: cancel any pending timer so scheduled callbacks
  // NEVER fire after the owning component tears down.
  //
  // Uses the project's sanctioned `useMountEffect` wrapper instead of a
  // raw `useEffect` — the hook itself is a reusable custom hook (the
  // only sanctioned home for `useEffect`), but `useMountEffect` is the
  // right primitive here because the cleanup semantics are "run once on
  // mount, tear down on unmount". `handle` is stable across renders
  // (memoized), so no dependency-array churn either. The flag is reset on
  // mount because StrictMode mounts, cleans up and mounts again.
  useMountEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      handle.clear();
    };
  });

  return handle;
}
